import { FC, Fragment, useEffect, useState } from "react";
// i18next
import { useTranslation } from "react-i18next";
// Redux
import { useAppDispatch, useAppSelector } from "app/hooks/useStore";
// Models
import IBillingInvoice from "app/models/BillingInvoice";
// Async
import { getInvoices } from "app/store/BillingInvoices/BillingInvoices.async";
// Selectors
import { selectInvoices, selectInvoicesTotal, selectParams } from "app/store/BillingInvoices/BillingInvoices.selectors";
// Components
import { Loader, Message } from "app/components/Utilities";
import InvoiceRow from "./InvoicesListItem";
// MUI
import {
  Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow
} from "@mui/material";

const BillingInvoicesList:FC = () => {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const invoices:IBillingInvoice[] | null = useAppSelector(selectInvoices);
  const params = useAppSelector(selectParams);
  const total = useAppSelector(selectInvoicesTotal);

  const [limit, setLimit] = useState<number>(params.limit);
  const [page, setPage] = useState<number>(0);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    const { dateRangeStart, dateRangeEnd, ...restParams } = params

    dispatch(getInvoices({ ...restParams, limit, offset: page * limit }));
    // eslint-disable-next-line
  }, [page, limit]);

  if ( !invoices ) return <Loader />;
  if ( !invoices.length ) return <Message text={t('pages.amBillingInvoices.noData')} />;
  return (
    <Fragment>
      <TableContainer component={Paper} sx={{ flexGrow: 1, height: 0 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ py: 1, px: 2 }}>{t('labels.invoiceNumber')}</TableCell>
              <TableCell sx={{ py: 1, px: 2 }}>{t('labels.billingPeriod')}</TableCell>
              <TableCell sx={{ py: 1, px: 2 }}>{t('labels.status')}</TableCell>
              <TableCell sx={{ py: 1, px: 2 }}>{t('labels.dueDate')}</TableCell>
              <TableCell sx={{ py: 1, px: 2 }}>{t('labels.amount')}</TableCell>
              <TableCell align="right" sx={{ py: 1, px: 2 }}>{t('labels.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map((invoice) => (
              <InvoiceRow key={invoice.id} invoice={invoice} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        sx={{ flexShrink: 0 }}
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={limit}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[50, 100, 200, 500]}
      />
    </Fragment>
  )
}

export default BillingInvoicesList;
